import React from 'react';
import { Link } from 'react-router-dom';
import RegistrationForm from '../components/RegistrationForm';

/**
 * Register page — split layout mirroring Login.js.
 *
 * Desktop (lg+): branded gradient panel on the left, form on the right.
 * Mobile: only the form panel is shown (no branded panel).
 */
function Register() {
  return (
    <div className="min-h-screen flex flex-row">
      {/* ---------------------------------------------------------------- */}
      {/* LEFT PANEL -- branded gradient (desktop only)                    */}
      {/* ---------------------------------------------------------------- */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 flex-col justify-between overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/10" />
        <div className="absolute top-1/2 -left-16 w-64 h-64 rounded-full bg-accent-500/15 -translate-y-1/2" />
        <div className="absolute bottom-0 right-1/4 w-56 h-56 rounded-full bg-primary-400/20 translate-y-1/3" />
        <div className="absolute bottom-1/3 -right-12 w-40 h-40 rounded-full bg-white/5" />

        {/* Logo */}
        <div className="relative z-10 p-10">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-soft-sm">
              <span className="text-white font-bold text-xl leading-none">C</span>
            </div>
            <span className="text-h2 text-white">
              Cli<span className="text-primary-200">QQ</span>
            </span>
          </Link>
        </div>

        {/* Hero copy */}
        <div className="relative z-10 px-10 flex-1 flex flex-col justify-center max-w-lg">
          <h2 className="text-display text-white mb-4 leading-tight">
            Join CliQQ.<br />Find your people.
          </h2>
          <p className="text-body-lg text-primary-100 mb-8">
            Become part of a growing community of explorers, creators, and
            connectors. Discover events that spark your curiosity and build
            friendships that last.
          </p>

          {/* Feature highlights */}
          <ul className="space-y-3">
            {[
              'Browse and join local events instantly',
              'Connect with like-minded people',
              'Create your own CliQQs and grow your audience',
            ].map((item) => (
              <li key={item} className="flex items-center gap-3 text-body-sm text-primary-100">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Copyright */}
        <div className="relative z-10 p-10">
          <p className="text-body-sm text-primary-300/70">
            &copy; {new Date().getFullYear()} CliQQ. All rights reserved.
          </p>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* RIGHT PANEL -- registration form                                 */}
      {/* ---------------------------------------------------------------- */}
      <div className="flex-1 flex items-start justify-center bg-surface-50 px-4 py-8 sm:px-8 overflow-y-auto">
        <div className="w-full max-w-lg">
          {/* Mobile logo (visible below lg) */}
          <div className="lg:hidden flex justify-center mb-8">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-soft-sm">
                <span className="text-white font-bold text-xl leading-none">C</span>
              </div>
              <span className="text-h2 text-surface-900">
                Cli<span className="text-primary-500">QQ</span>
              </span>
            </Link>
          </div>

          {/* Heading */}
          <h1 className="text-h1 text-surface-900 mb-1">Create your account</h1>
          <p className="text-body text-surface-500 mb-8">
            Set up your free CliQQ account in just a few steps.
          </p>

          {/* The multi-step registration form */}
          <RegistrationForm />

          {/* Sign-in link */}
          <p className="text-center text-body-sm text-surface-500 mt-8">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-semibold text-primary-600 hover:text-primary-700 transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
